import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Bot, Braces, Check, ChevronRight,
  CirclePlay, Code2, Cpu, Fingerprint, Gamepad2, GraduationCap,
  Layers3, LockKeyhole, MonitorPlay, Orbit, Palette, Play, Rocket,
  Scan, ShieldCheck, Sparkles, Terminal, Users, WandSparkles,
} from 'lucide-react';
import { useLocale } from '../i18n/LocaleProvider';
import { labCopy, labTrackKeys } from '../data/romindLabContent';
import './RomindLab.css';

const trackIcons = {
  coding: Code2,
  ai: Bot,
  cyber: ShieldCheck,
  games: Gamepad2,
  robotics: Cpu,
  aiArt: Palette,
  threeD: Layers3,
  animation: MonitorPlay,
  ui: Fingerprint,
  future: Orbit,
};

const previewSamples = {
  coding: {
    file: 'first-project.js',
    lines: ['const idea = "My first app";', 'const steps = ["design", "build", "test"];', '', 'steps.forEach(step => {', '  console.log(`✓ ${step}`);', '});'],
    output: '✓ design   ✓ build   ✓ test',
  },
  cyber: {
    file: 'safe-sandbox.js',
    lines: ['const sample = "demo-login";', 'const rules = ["length", "symbols"];', '', 'const checks = rules.map(rule =>', '  inspect(sample, rule)', ');'],
    output: 'DEMO / controlled scenario',
  },
  games: {
    file: 'game-loop.js',
    lines: ['const player = { x: 4, score: 0 };', 'const coin = { x: 5, points: 10 };', '', 'if (player.x + 1 === coin.x) {', '  player.score += coin.points;', '}'],
    output: 'SCORE +10   LEVEL 01',
  },
};

const previewTabs = ['coding', 'cyber', 'games'];

const SectionIntro = ({ label, title, description }) => (
  <div className="lab-section-intro">
    <span className="lab-eyebrow"><span className="lab-eyebrow-dot" />{label}</span>
    <h2>{title}</h2>
    {description && <p>{description}</p>}
  </div>
);

const RomindLab = () => {
  const { locale } = useLocale();
  const c = labCopy[locale] || labCopy.en;
  const [preview, setPreview] = useState('coding');
  const sample = previewSamples[preview];

  return (
    <main className="romind-lab-page" data-testid="romind-lab-page">
      <section className="lab-hero" aria-labelledby="lab-page-heading">
        <div className="lab-atmosphere lab-atmosphere-one" aria-hidden="true" />
        <div className="lab-atmosphere lab-atmosphere-two" aria-hidden="true" />
        <div className="lab-grid" aria-hidden="true" />
        <div className="lab-container lab-hero-grid">
          <div className="lab-hero-copy">
            <span className="lab-eyebrow"><span className="lab-eyebrow-dot" />{c.eyebrow}</span>
            <div className="lab-prelaunch-pill"><span />{c.status}</div>
            <h1 id="lab-page-heading">{c.heroLead}<span>{c.heroAccent}</span></h1>
            <p className="lab-hero-text">{c.heroBody}</p>
            <div className="lab-badges">
              {c.heroBadges.map((badge, index) => <span key={index}><Check size={13} />{badge}</span>)}
            </div>
            <div className="lab-actions">
              <a className="lab-button lab-button-primary" href="#directions">{c.exploreTracks}<ArrowRight size={17} /></a>
              <Link className="lab-button lab-button-secondary" to="/testing">{c.joinTesting}<ArrowUpRight size={16} /></Link>
            </div>
            <p className="lab-hero-disclaimer">{c.previewDisclaimer}</p>
          </div>

          <div className="lab-workbench" aria-label={c.previewA11y}>
            <div className="lab-workbench-glow" aria-hidden="true" />
            <div className="lab-workbench-bar">
              <div className="lab-window-dots" aria-hidden="true"><i /><i /><i /></div>
              <span><Terminal size={13} />{`ROMIND LAB / ${c.workbenchKicker}`}</span>
              <span className="lab-workbench-version">{'01 / DEMO'}</span>
            </div>
            <div className="lab-workbench-body">
              <div className="lab-workbench-head">
                <div><span className="lab-mini-label">{c.workbenchKicker}</span><h3>{c.workbenchTitle}</h3></div>
                <div className="lab-orbit-mark" aria-hidden="true"><Orbit size={25} /></div>
              </div>
              <p className="lab-workbench-caption">{c.workbenchText}</p>
              <div className="lab-workbench-tabs" role="group" aria-label={c.previewA11y}>
                {previewTabs.map((key) => (
                  <button type="button" key={key} onClick={() => setPreview(key)}
                    className={preview === key ? 'lab-tab-active' : ''}
                    aria-pressed={preview === key}>
                    {c.previewTabs[key]}
                  </button>
                ))}
              </div>
              <div className="lab-editor">
                <div className="lab-editor-title" dir="ltr"><Braces size={14} /><span>{sample.file}</span><span className="lab-editor-tag">JS</span></div>
                <div className="lab-code-lines" dir="ltr">
                  {sample.lines.map((line, index) => <div key={index}><span className="lab-line-number">{String(index + 1).padStart(2, '0')}</span><code>{line || '\u00a0'}</code></div>)}
                </div>
                <div className="lab-result"><span><CirclePlay size={13} />{c.exampleOutput}</span><code dir="ltr">{sample.output}</code></div>
              </div>
              <div className="lab-workbench-note"><Sparkles size={13} /><span>{c.workbenchFootnote}</span></div>
            </div>
          </div>
        </div>
        <div className="lab-hero-foot lab-container"><span>{c.heroFootline}</span><span>{c.heroFootMeta}</span></div>
      </section>

      <section className="lab-process lab-section" id="how-it-works">
        <div className="lab-container">
          <SectionIntro label={c.processLabel} title={c.processTitle} description={c.processIntro} />
          <div className="lab-process-grid">
            {c.steps.map((step, index) => {
              const icons = [GraduationCap, Code2, Play, Scan, Rocket];
              const Icon = icons[index];
              return <article className="lab-step" key={index}>
                <span className="lab-step-number">0{index + 1}</span>
                <div className="lab-step-icon"><Icon size={21} /></div>
                <h3>{step.title}</h3><p>{step.body}</p>
                {index < c.steps.length - 1 && <ChevronRight className="lab-step-arrow" size={17} aria-hidden="true" />}
              </article>;
            })}
          </div>
          <div className="lab-process-note"><span><WandSparkles size={17} />{c.mentorTitle}</span><p>{c.mentorBody}</p></div>
        </div>
      </section>

      <section className="lab-tracks lab-section" id="directions">
        <div className="lab-container">
          <SectionIntro label={c.tracksLabel} title={c.tracksTitle} description={c.tracksIntro} />
          <div className="lab-tracks-grid">
            {labTrackKeys.map((key, index) => {
              const Icon = trackIcons[key];
              const tr = c.tracks[index];
              return <article className="lab-track" key={key}>
                <div className="lab-track-top"><span className="lab-track-icon"><Icon size={25} strokeWidth={1.65} /></span><span className="lab-track-number">{String(index + 1).padStart(2, '0')} / 10</span></div>
                <h3>{tr.title}</h3>
                <p>{tr.body}</p>
                <div className="lab-track-outcome"><span>{c.outcomeLabel}</span><strong>{tr.outcome}</strong></div>
              </article>;
            })}
          </div>
          <p className="lab-catalog-note">{c.tracksFootnote}</p>
        </div>
      </section>

      <section className="lab-beyond lab-section">
        <div className="lab-container">
          <SectionIntro label={c.beyondLabel} title={c.beyondTitle} description={c.beyondIntro} />
          <div className="lab-feature-grid">
            {c.deepDives.map((item, index) => {
              const icons = [LockKeyhole, Gamepad2, Users];
              const Icon = icons[index];
              return <article className={`lab-feature lab-feature-${index}`} key={index}>
                <div className="lab-feature-head"><Icon size={26} /><span>0{index + 1}</span></div>
                <h3>{item.title}</h3><p>{item.body}</p>
                <ul>{item.points.map((point, i) => <li key={i}><Check size={14} />{point}</li>)}</ul>
              </article>;
            })}
          </div>
        </div>
      </section>

      <section className="lab-safety lab-section" id="safety">
        <div className="lab-container lab-safety-grid">
          <div><SectionIntro label={c.safetyLabel} title={c.safetyTitle} description={c.safetyIntro} />
            <Link className="lab-inline-link" to="/child-safety">{c.safetyLink}<ArrowRight size={16} /></Link>
          </div>
          <div className="lab-safety-list">
            {c.safetyPoints.map((p, index) => {
              const icons = [ShieldCheck, LockKeyhole, Bot, Users];
              const Icon = icons[index];
              return <div className="lab-safety-item" key={index}><div><Icon size={20} /></div><section><h3>{p.title}</h3><p>{p.body}</p></section></div>;
            })}
          </div>
        </div>
      </section>

      <section className="lab-future lab-section">
        <div className="lab-container">
          <SectionIntro label={c.futureLabel} title={c.futureTitle} description={c.futureIntro} />
          <div className="lab-roadmap">
            {c.futureSteps.map((p, index) => <div className="lab-roadmap-item" key={index}>
              <span className={`lab-roadmap-state ${index === 0 ? 'lab-roadmap-current' : ''}`}>{index === 0 ? c.nowTag : c.futureTag}</span>
              <div className="lab-roadmap-node">{index + 1}</div><h3>{p.title}</h3><p>{p.body}</p>
            </div>)}
          </div>
          <div className="lab-future-disclaimer"><ShieldCheck size={17} /><p>{c.futureDisclaimer}</p></div>
          <div className="lab-future-actions">
            <Link to="/academy" className="lab-inline-link">{c.academyLink}<ArrowUpRight size={16} /></Link>
            <Link to="/venture-studio" className="lab-inline-link">{c.ventureLink}<ArrowUpRight size={16} /></Link>
            <Link to="/ecosystem" className="lab-inline-link">{c.ecosystemLink}<ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>

      <section className="lab-cta lab-section">
        <div className="lab-container">
          <div className="lab-cta-inner"><div className="lab-cta-glow" aria-hidden="true" />
            <div className="lab-cta-icon"><Rocket size={31} /></div>
            <span className="lab-eyebrow">{`ROMIND LAB / ${c.futureLabel}`}</span>
            <h2>{c.ctaTitle}</h2><p>{c.ctaBody}</p>
            <div className="lab-actions lab-actions-centered"><Link className="lab-button lab-button-primary" to="/testing">{c.joinTesting}<ArrowRight size={17} /></Link><Link className="lab-button lab-button-secondary" to="/contact">{c.contact}<ArrowUpRight size={16} /></Link></div>
            <span className="lab-cta-caption">{c.ctaFootnote}</span>
          </div>
        </div>
      </section>
    </main>
  );
};

export default RomindLab;
